import React, { useEffect, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { StyledFlex } from '@goodfynd/react-web.styles';
import { Text } from '@goodfynd/react-web.typography.text';
import { Icon } from '@goodfynd/react-web.ui.icon';
import { Loading } from '@goodfynd/react-web.ui.loading';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { GoogleMap, Marker } from '@react-google-maps/api';

import config, { unicode } from '../../../config';
import { useGeoInput } from '../../../hooks/useGeoInput';

import { StyledFieldContainer, StyledLocationButton } from './styles';
import type { GeoAutocompleteProps } from './types';

export default function GeoAutocomplete<T extends FieldValues>({
  autoComplete,
  autoFocus,
  defaultValue,
  disabled,
  draggable = true,
  enableFriendlyName,
  error,
  fullWidth = true,
  helperText,
  label,
  lookup = false,
  mapContainerStyle,
  onChange,
  onSelect,
  placeholder = 'Search address, city, state, zip...',
  textFieldProps,
  required = false,
  showAddressFields = false,
  showMap = false,
  updatedLocation,
  value,
  ...props
}: GeoAutocompleteProps<T>) {
  const textRef = useRef<HTMLInputElement>();

  const {
    location,
    selectLocation,
    setLocationName,
    query,
    suggestions,
    setQuery,
    ready,
    setLatLng,
  } = useGeoInput(value as JsonLocation, lookup);
  const [center, setCenter] = useState<google.maps.LatLngLiteral>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!ready) {
      return;
    }

    const locationUpdate = location;
    onChange(locationUpdate);
    onSelect?.(locationUpdate);
    if (locationUpdate) {
      const [lat, lng] = locationUpdate.coordinates.split(',').map(Number);
      setCenter({
        lat,
        lng,
      });
    }
  }, [location, onChange, onSelect, ready]);

  useEffect(() => {
    if (ready && updatedLocation) {
      onChange(updatedLocation);
      console.debug('Schedule location update: ', updatedLocation);
      const [lat, lng] = updatedLocation.coordinates.split(',').map(Number);
      setCenter({
        lat,
        lng,
      });
    }

    if (updatedLocation && lookup) {
      selectLocation({
        label: updatedLocation.address || updatedLocation.coordinates,
        name: updatedLocation.name,
        value: '',
      });
    }
  }, [lookup, onChange, ready, selectLocation, updatedLocation]);

  const handleGetLocation = () => {
    setLoading(true);
    textRef.current?.blur();
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatLng({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        console.error('😱 Error: ', error);
        textRef.current?.blur();
      }
    );
  };

  return (
    <StyledFieldContainer>
      <Autocomplete<SelectOption | string>
        disabled={disabled}
        fullWidth={fullWidth}
        inputValue={query}
        loading={!ready}
        options={suggestions}
        popupIcon={null}
        getOptionLabel={(option: string | SelectOption) =>
          typeof option === 'string'
            ? option
            : [option.label, option.subLabel]
                .filter(Boolean)
                .join(unicode.centerDotPadded)
        }
        noOptionsText={
          lookup ? (
            <StyledLocationButton onClick={handleGetLocation}>
              <Icon name="location" />
              Use Current Location
            </StyledLocationButton>
          ) : null
        }
        onChange={(_, option) => {
          const selected = option as SelectOption;
          selectLocation(selected);
        }}
        renderInput={(params) => (
          <TextField
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            required={required}
            {...props}
            {...textFieldProps}
            {...params}
            error={!!error}
            helperText={error?.message || helperText}
            inputRef={textRef}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="location_pin" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading && <Loading type="circle" />}
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <StyledFlex
              gap={2}
              css={{
                alignItems: 'flex-start',
                flexDirection: 'column',
              }}
            >
              {typeof option === 'string' ? (
                <Text>{option}</Text>
              ) : (
                <>
                  <Text>{option.label}</Text>
                  {option.subLabel && (
                    <Text type="caption">{option.subLabel}</Text>
                  )}
                </>
              )}
            </StyledFlex>
          </li>
        )}
      />

      {enableFriendlyName && location && (
        <TextField
          helperText="Input a friendly name to save this location for future use?"
          label="Friendly name i.e. Farragut Square"
          onChange={(e) => setLocationName(e.target.value)}
          value={location.name || ''}
          variant="outlined"
        />
      )}

      {showAddressFields && (
        <>
          {!!location?.city && (
            <TextField
              disabled
              label="City"
              value={location.city}
              variant="outlined"
            />
          )}

          {!!location?.county && (
            <TextField
              disabled
              label="County"
              value={location.county}
              variant="outlined"
            />
          )}

          {!!location?.state && (
            <TextField
              disabled
              label="State"
              value={location.state}
              variant="outlined"
            />
          )}
        </>
      )}

      {showMap && center && ready && (
        <GoogleMap
          center={center}
          zoom={17}
          mapContainerStyle={{
            height: '249px',
            width: '100%',
            ...mapContainerStyle,
          }}
        >
          <Marker
            draggable={draggable}
            icon={config.env.GOOGLE_MAPS_ICON}
            position={center}
            onDragEnd={
              draggable
                ? ({ latLng }) => {
                    if (latLng) {
                      setCenter({
                        lat: latLng.lat(),
                        lng: latLng.lng(),
                      });
                      setLatLng({
                        lat: latLng.lat(),
                        lng: latLng.lng(),
                      });
                    }
                  }
                : undefined
            }
          />
        </GoogleMap>
      )}
    </StyledFieldContainer>
  );
}
